import React, { useEffect, useState } from "react";
import { Breadcrumb, Layout, Table, Button, Input, Space, Tag, Card, Typography, Spin, Select, Badge, Modal, notification } from "antd";
import { useAuth } from "../../hooks/useAuth";
import { FormattedMessage, useIntl } from "react-intl";
import { SearchOutlined, EyeOutlined, PlusOutlined, ArrowLeftOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from "react-router-dom";
import { IGallery } from "../../services/nestjs-base-api/types";
import {
  useGetOrganisationByIdQuery,
  useGetAdminOrganisationEventsQuery,
  useGetEventAllGalleriesQuery,
  useGetGalleryDetailsQuery
} from "../../services/nestjs-base-api/admin-api";
import dayjs from 'dayjs';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Search } = Input;

const AdminEventGalleriesPage: React.FunctionComponent = () => {
  const intl = useIntl();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { organisationId, eventId } = useParams<{ organisationId: string, eventId: string }>();
  const [searchText, setSearchText] = useState("");
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [expandedGalleryId, setExpandedGalleryId] = useState<number | null>(null);
  
  // Fetch organisation details
  const {
    data: organisation,
    error: organisationError,
    isLoading: isLoadingOrganisation
  } = useGetOrganisationByIdQuery({
    jwtToken: user.jwtToken,
    organisationId: Number(organisationId)
  }, { skip: !organisationId });
  
  // Fetch events for organisation
  const {
    data: events,
    error: eventsError,
    isLoading: isLoadingEvents
  } = useGetAdminOrganisationEventsQuery({
    jwtToken: user.jwtToken,
    organisationId: Number(organisationId)
  }, { skip: !organisationId });
  
  // Get current event
  const currentEvent = events?.find(event => event.id === Number(eventId));
  
  // Fetch galleries for the specific event
  const {
    data: galleries,
    error: galleriesError,
    isLoading: isLoadingGalleries
  } = useGetEventAllGalleriesQuery({
    jwtToken: user.jwtToken,
    eventId: Number(eventId)
  });

  // Fetch gallery details when expanded
  const {
    data: galleryDetails
  } = useGetGalleryDetailsQuery(
    { jwtToken: user.jwtToken, galleryId: expandedGalleryId || 0 },
    { skip: !expandedGalleryId }
  );

  useEffect(() => {
    if (organisationError || eventsError || galleriesError) {
      if (
        (organisationError && 'status' in organisationError && organisationError.status === 401) ||
        (eventsError && 'status' in eventsError && eventsError.status === 401) ||
        (galleriesError && 'status' in galleriesError && galleriesError.status === 401)
      ) {
        logout();
      }
    }
  }, [organisationError, eventsError, galleriesError, logout]);

  // Use galleries data directly
  const processedGalleries = galleries || [];

  console.log("Galleries data:", galleries);

  // Filter by search text and type if we have galleries
  const searchAndTypeFiltered = processedGalleries.filter((gallery: IGallery) => {
    const matchesSearch = searchText ?
      gallery.name.toLowerCase().includes(searchText.toLowerCase()) ||
      (gallery.description && gallery.description.toLowerCase().includes(searchText.toLowerCase())) :
      true;

    const matchesType = typeFilter ?
      gallery.type === typeFilter :
      true;

    return matchesSearch && matchesType;
  });

  // Navigate back to organisation events
  const goBackToEvents = () => {
    navigate(`/admin/organisations/${organisationId}/events`);
  };
  
  // Navigate back to organisations
  const goBackToOrganisations = () => {
    navigate('/admin/organisations');
  };

  // Handle gallery view
  const handleViewGallery = (id: number) => {
    navigate(`/admin/organisations/${organisationId}/events/${eventId}/galleries/${id}`);
  };

  // Handle gallery deletion with confirmation
  const handleDeleteGallery = (id: number, name: string) => {
    Modal.confirm({
      title: intl.formatMessage(
        { id: 'gallery.delete.confirm.title' },
        { name }
      ) || `Delete Gallery: ${name}`,
      content: (
        <>
          <p>
            {intl.formatMessage({ id: 'gallery.delete.confirm.content' }) ||
              'This will permanently delete the gallery and all its photos. This action cannot be undone.'}
          </p>
          <p style={{ fontWeight: 'bold' }}>"{name}"</p>
        </>
      ),
      okText: intl.formatMessage({ id: 'common.delete' }) || 'Delete',
      cancelText: intl.formatMessage({ id: 'common.cancel' }) || 'Cancel',
      okButtonProps: { danger: true },
      onOk: () => {
        // In a real implementation, this would call the API to delete the gallery
        notification.success({
          message: intl.formatMessage({ id: 'admin.galleries.deleteSuccess' }) || 'Gallery Deleted',
          description: intl.formatMessage(
            { id: 'admin.galleries.deleteSuccessDesc' },
            { name }
          ) || `Gallery "${name}" has been successfully deleted.`,
        });
      },
    });
  };

  // Function to get gallery type color
  const getGalleryTypeColor = (type: string): string => {
    switch (type) {
      case 'GENERAL':
        return 'blue';
      case 'VIP':
        return 'gold';
      case 'ATTENDEES':
        return 'green';
      case 'STAFF':
        return 'cyan';
      case 'PERFORMERS':
        return 'purple';
      default:
        return 'default';
    }
  };

  // Expanded row render with gallery details
  const expandedRowRender = (record: IGallery) => {
    const photoCount = galleryDetails?.photoCount || 0;

    return (
      <div style={{ padding: '20px' }}>
        <Title level={5}>Gallery Details</Title>

        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <div><strong>Gallery ID:</strong> {record.id}</div>
          <div><strong>Hash:</strong> {record.hash}</div>
          <div><strong>Created At:</strong> {dayjs(record.createdAt).format('YYYY-MM-DD HH:mm:ss')}</div>
          <div><strong>Updated At:</strong> {dayjs(record.updatedAt).format('YYYY-MM-DD HH:mm:ss')}</div>
          <div>
            <strong>Description:</strong> {record.description || 'No description provided'}
          </div>
          <div>
            <Badge count={photoCount} showZero>
              <span style={{ padding: '0 8px' }}><strong>Photos:</strong></span>
            </Badge>
          </div>
        </Space>

      </div>
    );
  };

  // Get unique gallery types for filter
  const galleryTypes = galleries
    ? Array.from(new Set(galleries.map((gallery) => gallery.type)))
    : [];

  // Table columns
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a: IGallery, b: IGallery) => a.id - b.id,
      width: 80,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: IGallery, b: IGallery) => a.name.localeCompare(b.name),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (
        <Tag color={getGalleryTypeColor(type)}>
          {type}
        </Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
      sorter: (a: IGallery, b: IGallery) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
    },
    {
      title: 'Photos',
      key: 'photos',
      render: (_: any, record: IGallery) => (
        <Button 
          type="link" 
          onClick={() => handleViewGallery(record.id)}
        >
          View Photos
        </Button>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: IGallery) => (
        <Space size="small">
          <Button
            icon={<EyeOutlined />}
            onClick={() => handleViewGallery(record.id)}
            title="View"
          />
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteGallery(record.id, record.name)}
            title="Delete"
          />
        </Space>
      ),
    },
  ];

  // Loading state
  if (isLoadingOrganisation || isLoadingEvents) {
    return (
      <Content style={{ margin: '0 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
          <Spin size="large" />
          <Text style={{ marginLeft: 16 }}>Loading details...</Text>
        </div>
      </Content>
    );
  }

  // Debug information
  console.log("Event ID:", eventId);
  console.log("Galleries data:", galleries);
  console.log("Filtered galleries:", searchAndTypeFiltered);

  return (
    <Content style={{ margin: '0 16px' }}>
      <Breadcrumb style={{ margin: '16px 0' }}>
        <Breadcrumb.Item>RecogniX</Breadcrumb.Item>
        <Breadcrumb.Item>
          <FormattedMessage id={'section.adminDashboardName'} defaultMessage="Admin Dashboard" />
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <a onClick={goBackToOrganisations}>
            <FormattedMessage id={'section.organisationsName'} defaultMessage="Organisations" />
          </a>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <a onClick={goBackToEvents}>
            {organisation?.name}
          </a>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          {currentEvent?.name || "Event"}
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <FormattedMessage id={'section.galleriesName'} defaultMessage="Galleries" />
        </Breadcrumb.Item>
      </Breadcrumb>
      
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Button 
              icon={<ArrowLeftOutlined />} 
              style={{ marginRight: 16 }} 
              onClick={goBackToEvents}
            >
              Back to Events
            </Button>
            <Title level={2} style={{ margin: 0 }}>
              {currentEvent?.name || "Event"} - <FormattedMessage id={'section.galleriesName'} defaultMessage="Galleries" />
            </Title>
          </div>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
            <Search
              placeholder={intl.formatMessage({ id: 'admin.galleries.searchPlaceholder' }) || "Search galleries by name or description"}
              allowClear
              enterButton={<SearchOutlined />}
              onSearch={value => setSearchText(value)}
              style={{ width: 300 }}
            />
            
            <Space>
              <Select
                placeholder={intl.formatMessage({ id: 'admin.galleries.typeFilter' }) || "Filter by type"}
                style={{ width: 150 }}
                allowClear
                onChange={(value) => setTypeFilter(value)}
              >
                {galleryTypes.map((type) => (
                  <Select.Option key={type} value={type}>
                    {type}
                  </Select.Option>
                ))}
              </Select>
              
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate(`/dashboard/events/${eventId}/galleries/create`)}
              >
                Create Gallery
              </Button>
            </Space>
          </div>
          
          <Table
            dataSource={searchAndTypeFiltered}
            columns={columns}
            rowKey="id"
            loading={isLoadingGalleries}
            pagination={{ pageSize: 10 }}
            locale={{ emptyText: "No galleries found for this event" }}
            expandable={{
              expandedRowRender,
              onExpand: (expanded, record) => {
                if (expanded) {
                  setExpandedGalleryId(record.id);
                } else {
                  setExpandedGalleryId(null);
                }
              }
            }}
          />
        </Space>
      </Card>
    </Content>
  );
};

export default AdminEventGalleriesPage;